import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Loader2, Bot, CheckCircle2, XCircle, RefreshCw, Database,
  Clock, AlertCircle, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus,
  Pencil, Eye, Check, X, ChevronLeft, ChevronRight, Search, SlidersHorizontal, Filter,
} from "lucide-react";
import { WorkModelBadge } from "@/components/jobs/WorkModelBadge";
import { DeadlineBadge } from "@/components/jobs/DeadlineBadge";
import { SourceBadge } from "@/components/jobs/SourceBadge";
import { getJobQuality, QUALITY_STYLES } from "@/lib/jobQuality";
import { Progress } from "@/components/ui/progress";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { toast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useJobLocations } from "@/hooks/useJobLocations";
import { CityCombobox } from "@/components/admin/CityCombobox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CRAWLER_ORG_ID = "00000000-0000-0000-0000-000000000001";

const statusConfig: Record<string, { label: string; icon: typeof CheckCircle2; color: string }> = {
  completed: { label: "Concluído", icon: CheckCircle2, color: "text-green-500" },
  failed: { label: "Falhou", icon: XCircle, color: "text-destructive" },
  running: { label: "Em Execução", icon: RefreshCw, color: "text-primary" },
};

// Enrichment-specific status mapping
const enrichmentStatusColors: Record<string, string> = {
  pending_review: "text-orange-500",
  rascunho: "text-orange-500",
  em_revisao: "text-primary",
  publicada: "text-green-600",
  descartada: "text-destructive",
  pausada: "text-muted-foreground",
  encerrada: "text-muted-foreground",
};
const enrichmentStatusLabels: Record<string, string> = {
  pending_review: "Em enriquecimento",
  rascunho: "Em enriquecimento",
  em_revisao: "Aprovada p/ revisão",
  publicada: "Publicada",
  descartada: "Descartada",
  pausada: "Pausada",
  encerrada: "Encerrada",
};

const ENRICHMENT_STATUS_OPTIONS = [
  { value: "all", label: "Todos os status" },
  { value: "pending_review", label: "Em enriquecimento" },
  { value: "em_revisao", label: "Aprovada p/ revisão" },
  { value: "publicada", label: "Publicada" },
  { value: "descartada", label: "Descartada" },
];

// Statuses where enrichment actions (edit, approve, discard) are available
const ENRICHMENT_ACTIONABLE_STATUSES = ["pending_review", "rascunho"];

const MATCH_RANK_LABELS: Record<number, string> = {
  1: "titulo",
  2: "empresa",
  3: "resumo",
  4: "local",
  5: "skills",
  6: "descricao",
};

const PAGE_SIZE_OPTIONS = [20, 50, 100, 500] as const;

function formatDuration(startedAt: string, finishedAt: string | null): string {
  if (!finishedAt) return "Em andamento";
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  return `${mins}min ${secs % 60}s`;
}

function startOfWeek(daysAgo: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(0, 0, 0, 0);
  return d;
}

function TrendBadge({ current, previous }: { current: number; previous: number }) {
  if (previous === 0 && current === 0) return null;
  const diff = current - previous;
  if (diff === 0) return <span className="text-xs text-muted-foreground flex items-center gap-0.5"><Minus className="w-3 h-3" /> igual</span>;
  if (diff > 0) return <span className="text-xs text-green-600 flex items-center gap-0.5"><TrendingUp className="w-3 h-3" /> +{diff}</span>;
  return <span className="text-xs text-destructive flex items-center gap-0.5"><TrendingDown className="w-3 h-3" /> {diff}</span>;
}

export default function AdminCrawlerDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());
  const [periodDays, setPeriodDays] = useState<7 | 30 | null>(null);

  // Enrichment table state
  const [enrichmentStatus, setEnrichmentStatus] = useState("pending_review");
  const [pageSize, setPageSize] = useState<number>(20);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmDiscard, setConfirmDiscard] = useState<{ type: "single"; id: string } | { type: "batch"; ids: string[] } | null>(null);
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const [cityFilter, setCityFilter] = useState<string | null>(null);
  const [qualityFilter, setQualityFilter] = useState<string>("all");

  // Debounce search — 350ms delay before querying
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 350);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchTerm]);

  const { data: runs, isLoading } = useQuery({
    queryKey: ["crawler_runs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crawler_runs")
        .select("*")
        .order("started_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data;
    },
  });

  const { data: pendingCount } = useQuery({
    queryKey: ["crawler_pending_count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("job_posts")
        .select("*", { count: "exact", head: true })
        .eq("org_id", CRAWLER_ORG_ID)
        .eq("status", "em_revisao");
      if (error) throw error;
      return count ?? 0;
    },
  });

  const { data: publishedCount } = useQuery({
    queryKey: ["crawler_published_count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("job_posts")
        .select("*", { count: "exact", head: true })
        .eq("org_id", CRAWLER_ORG_ID)
        .eq("status", "publicada");
      if (error) throw error;
      return count ?? 0;
    },
  });

  // --- Mutual exclusion handlers: search vs geo filters ---
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value.trim() && (stateFilter || cityFilter)) {
      setStateFilter(null);
      setCityFilter(null);
      toast({ title: "Filtros de localidade removidos", description: "A busca pesquisa em todos os campos." });
    }
  };
  const handleStateChange = (value: string) => {
    setStateFilter(value === "all" ? null : value);
    setCityFilter(null);
    if (searchTerm.trim()) {
      setSearchTerm("");
      setDebouncedSearch("");
      toast({ title: "Busca removida", description: "Filtros de localidade aplicados." });
    }
    setPage(0);
  };
  const handleCityChange = (value: string | null) => {
    setCityFilter(value);
    if (searchTerm.trim()) {
      setSearchTerm("");
      setDebouncedSearch("");
      toast({ title: "Busca removida", description: "Filtros de localidade aplicados." });
    }
    setPage(0);
  };

  // Paginated enrichment jobs query — uses RPC for full-text search, regular query otherwise
  const isSearching = debouncedSearch.trim().length > 0;
  const isQualityFiltering = qualityFilter !== "all";

  const { data: enrichmentData, isFetching: isEnrichmentFetching } = useQuery({
    queryKey: ["crawler_enrichment_jobs", enrichmentStatus, pageSize, page, debouncedSearch, stateFilter, cityFilter, qualityFilter],
    queryFn: async () => {
      // Full-text search via RPC
      if (isSearching) {
        const { data, error } = await (supabase.rpc as any)("search_crawler_jobs", {
          search_term: debouncedSearch.trim(),
          status_filter: enrichmentStatus,
          result_limit: isQualityFiltering ? 500 : pageSize,
          result_offset: isQualityFiltering ? 0 : page * pageSize,
        });
        if (error) throw error;
        const rows = (data ?? []) as any[];
        const totalCount = rows.length > 0 ? Number(rows[0].total_count) : 0;
        return {
          jobs: rows.map((r: any) => ({
            id: r.id,
            title: r.title,
            description: r.description,
            employment_type: r.employment_type,
            work_model: r.work_model,
            location_city: r.location_city,
            location_state: r.location_state,
            responsibilities: r.responsibilities,
            requirements_must: r.requirements_must,
            benefits: r.benefits,
            stipend_min: r.stipend_min,
            stipend_max: r.stipend_max,
            skills_tags: r.skills_tags,
            deadline_date: r.deadline_date,
            status: r.status,
            created_at: r.created_at,
            crawler_metadata: [{ company_name: r.company_name, source_name: r.source_name, source_url: r.source_url, external_apply_url: r.external_apply_url }],
            _match_rank: r.match_rank,
          })),
          totalCount,
        };
      }

      // Regular paginated query (no search)
      const fetchLimit = isQualityFiltering ? 500 : pageSize;
      const fetchOffset = isQualityFiltering ? 0 : page * pageSize;
      let query = supabase
        .from("job_posts")
        .select("id, title, description, employment_type, work_model, location_city, location_state, responsibilities, requirements_must, benefits, stipend_min, stipend_max, skills_tags, deadline_date, status, created_at, crawler_metadata(company_name, source_name, source_type, source_url, external_apply_url)", { count: "exact" })
        .eq("org_id", CRAWLER_ORG_ID)
        .order("created_at", { ascending: false })
        .range(fetchOffset, fetchOffset + fetchLimit - 1);

      if (enrichmentStatus !== "all") {
        if (enrichmentStatus === "pending_review") {
          query = query.in("status", ["pending_review", "rascunho"]);
        } else {
          query = query.eq("status", enrichmentStatus);
        }
      }
      if (stateFilter) query = query.eq("location_state", stateFilter);
      if (cityFilter) query = query.eq("location_city", cityFilter);

      const { data, error, count } = await query;
      if (error) throw error;
      return { jobs: data ?? [], totalCount: count ?? 0 };
    },
    placeholderData: (prev) => prev,
  });

  // Client-side quality filter + hybrid pagination
  const { filteredEnrichmentJobs, displayTotal, totalPages } = useMemo(() => {
    const raw = enrichmentData?.jobs ?? [];
    const serverTotal = enrichmentData?.totalCount ?? 0;
    if (!isQualityFiltering) {
      return { filteredEnrichmentJobs: raw, displayTotal: serverTotal, totalPages: Math.max(1, Math.ceil(serverTotal / pageSize)) };
    }
    const qualityFiltered = raw.filter((j) => getJobQuality(j).tier === qualityFilter);
    const sliced = qualityFiltered.slice(page * pageSize, (page + 1) * pageSize);
    return {
      filteredEnrichmentJobs: sliced,
      displayTotal: qualityFiltered.length,
      totalPages: Math.max(1, Math.ceil(qualityFiltered.length / pageSize)),
    };
  }, [enrichmentData, qualityFilter, isQualityFiltering, page, pageSize]);
  const totalEnrichmentCount = displayTotal;

  // Clear selection when page, filter, or search changes
  useEffect(() => {
    setSelected(new Set());
  }, [page, enrichmentStatus, debouncedSearch, stateFilter, cityFilter, qualityFilter]);

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const actionableIds = useMemo(
    () => filteredEnrichmentJobs.filter((j) => ENRICHMENT_ACTIONABLE_STATUSES.includes(j.status)).map((j) => j.id),
    [filteredEnrichmentJobs],
  );

  const selectAllVisible = useCallback(() => {
    setSelected((prev) => {
      const allSelected = actionableIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        actionableIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...actionableIds]);
    });
  }, [actionableIds]);

  // Cross-page select all (fetches all IDs for current filter)
  const selectAllCrossPage = useCallback(async () => {
    if (selected.size >= totalEnrichmentCount && totalEnrichmentCount > 0) {
      setSelected(new Set());
      return;
    }
    const MAX = 500;
    const offset = page * pageSize;
    const buildQuery = () => {
      let q = supabase.from("job_posts").select("id").eq("org_id", CRAWLER_ORG_ID);
      if (enrichmentStatus !== "all") {
        if (enrichmentStatus === "pending_review") {
          q = q.in("status", ["pending_review", "rascunho"]);
        } else {
          q = q.eq("status", enrichmentStatus);
        }
      }
      return q;
    };
    const { data: fromCurrent } = await buildQuery().range(offset, offset + MAX - 1);
    let ids = (fromCurrent ?? []).map((d) => d.id);
    if (ids.length < MAX && offset > 0) {
      const remaining = MAX - ids.length;
      const { data: fromStart } = await buildQuery().range(0, remaining - 1);
      const currentSet = new Set(ids);
      const extra = (fromStart ?? []).map((d) => d.id).filter((id) => !currentSet.has(id));
      ids = [...ids, ...extra].slice(0, MAX);
    }
    setSelected(new Set(ids));
  }, [enrichmentStatus, selected.size, totalEnrichmentCount, page, pageSize]);

  const batchUpdate = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: string }) => {
      const CHUNK = 200;
      for (let i = 0; i < ids.length; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK);
        const { error } = await supabase.from("job_posts").update({ status }).in("id", chunk);
        if (error) throw error;
      }
    },
    onSuccess: (_, { ids, status }) => {
      queryClient.invalidateQueries({ queryKey: ["crawler_enrichment_jobs"] });
      queryClient.invalidateQueries({ queryKey: ["crawler_pending_count"] });
      queryClient.invalidateQueries({ queryKey: ["crawler_published_count"] });
      setSelected(new Set());
      toast({ title: `${ids.length} vaga(s) ${status === "em_revisao" ? "enviadas para revisão" : "descartadas"}` });
    },
    onError: (err) => {
      toast({ title: "Erro ao atualizar vagas", description: (err as Error).message, variant: "destructive" });
    },
  });

  const someSelected = selected.size > 0;

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const update: any = { status };
      if (status === "publicada") update.published_at = new Date().toISOString();
      const { error } = await supabase.from("job_posts").update(update).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: ["crawler_enrichment_jobs"] });
      queryClient.invalidateQueries({ queryKey: ["crawler_pending_count"] });
      queryClient.invalidateQueries({ queryKey: ["crawler_published_count"] });
      toast({ title: status === "descartada" ? "Vaga descartada" : "Vaga enviada para revisão" });
    },
    onError: (err) => {
      toast({ title: "Erro ao atualizar vaga", description: (err as Error).message, variant: "destructive" });
    },
  });

  // Location data for filters
  const { states: availableStates, cities: availableCities } = useJobLocations({
    orgId: CRAWLER_ORG_ID,
    selectedState: stateFilter,
  });

  const totalInserted = runs?.reduce((sum, r) => sum + (r.total_inserted ?? 0), 0) ?? 0;
  const lastRun = runs?.[0] ?? null;

  const thisWeekStart = startOfWeek(7);
  const lastWeekStart = startOfWeek(14);
  const thisWeekRuns = runs?.filter((r) => new Date(r.started_at) >= thisWeekStart) ?? [];
  const lastWeekRuns = runs?.filter((r) => { const d = new Date(r.started_at); return d >= lastWeekStart && d < thisWeekStart; }) ?? [];
  const thisWeekInserted = thisWeekRuns.reduce((s, r) => s + (r.total_inserted ?? 0), 0);
  const lastWeekInserted = lastWeekRuns.reduce((s, r) => s + (r.total_inserted ?? 0), 0);
  const thisWeekErrors = thisWeekRuns.reduce((s, r) => s + (r.total_errors ?? 0), 0);

  const total = (publishedCount ?? 0) + (pendingCount ?? 0);
  const approvalRate = total > 0 ? Math.round(((publishedCount ?? 0) / total) * 100) : null;

  const toggleError = (id: string) => {
    setExpandedErrors((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  if (isLoading) {
    return <div className="p-8 flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="p-8 max-w-5xl">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <Bot className="w-6 h-6 text-primary" />
          <h1 className="font-heading text-2xl font-bold text-foreground">Robô de Vagas</h1>
        </div>
        <p className="text-muted-foreground text-sm">Histórico de execuções e enriquecimento de vagas coletadas</p>
      </motion.div>

      {/* Stats */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-card rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><Database className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-2xl font-bold text-foreground">{totalInserted.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Total importado</p>
              <TrendBadge current={thisWeekInserted} previous={lastWeekInserted} />
            </div>
          </div>
        </div>

        <div className="bg-card rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/vagas")} className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center shrink-0 hover:bg-yellow-500/20 transition-colors">
              <AlertCircle className="w-5 h-5 text-yellow-500" />
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-bold text-foreground">{pendingCount ?? 0}</p>
              <p className="text-xs text-muted-foreground">Aguardando revisão</p>
              <button onClick={() => navigate("/admin/vagas")} className="text-xs text-primary hover:underline">Ver vagas →</button>
            </div>
          </div>
        </div>

        <button onClick={() => navigate("/admin/vagas-ativas")} className="bg-card rounded-2xl p-5 border border-border hover:border-green-400 transition-colors text-left">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-green-500" /></div>
            <div>
              <p className="text-2xl font-bold text-foreground">{publishedCount ?? 0}</p>
              <p className="text-xs text-muted-foreground">Publicadas</p>
              {approvalRate !== null && <span className="text-xs text-muted-foreground">{approvalRate}% aprovação</span>}
            </div>
          </div>
        </button>

        <div className="bg-card rounded-2xl p-5 border border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center"><Clock className="w-5 h-5 text-muted-foreground" /></div>
            <div>
              <p className="text-2xl font-bold text-foreground">{runs?.length ?? 0}</p>
              <p className="text-xs text-muted-foreground">Execuções</p>
              {thisWeekErrors > 0 && <span className="text-xs text-destructive">{thisWeekErrors} erros esta semana</span>}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Last run */}
      {lastRun && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card rounded-2xl p-5 border border-border mb-6">
          <h2 className="font-semibold text-sm text-muted-foreground mb-3 uppercase tracking-wide">Última execução</h2>
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <p className="text-sm font-medium text-foreground">{lastRun.source}</p>
              <p className="text-xs text-muted-foreground">{new Date(lastRun.started_at).toLocaleString("pt-BR")}</p>
            </div>
            {lastRun.status && (() => {
              const cfg = statusConfig[lastRun.status] ?? { label: lastRun.status, icon: Bot, color: "text-muted-foreground" };
              const Icon = cfg.icon;
              return <div className={`flex items-center gap-1 ${cfg.color}`}><Icon className="w-4 h-4" /><span className="text-sm font-medium">{cfg.label}</span></div>;
            })()}
            <div className="flex gap-6 text-sm text-muted-foreground ml-auto flex-wrap">
              <span><strong className="text-foreground">{lastRun.total_fetched ?? 0}</strong> coletadas</span>
              <span><strong className="text-foreground">{lastRun.total_inserted ?? 0}</strong> inseridas</span>
              <span><strong className="text-foreground">{lastRun.total_dupes ?? 0}</strong> duplicatas</span>
              <span className={lastRun.total_errors ? "text-destructive" : ""}><strong>{lastRun.total_errors ?? 0}</strong> erros</span>
              <span>{formatDuration(lastRun.started_at, lastRun.finished_at)}</span>
            </div>
          </div>
        </motion.div>
      )}

      {/* Runs table */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-4 flex-wrap">
          <h2 className="font-semibold text-foreground">Histórico de execuções</h2>
          <div className="flex items-center gap-1">
            {([7, 30, null] as const).map((d) => (
              <button key={String(d)} onClick={() => setPeriodDays(d)} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${periodDays === d ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                {d === null ? "Tudo" : `${d} dias`}
              </button>
            ))}
          </div>
        </div>
        {!runs || runs.length === 0 ? (
          <div className="p-12 text-center">
            <Bot className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Nenhuma execução registrada ainda.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="text-left p-4 font-medium text-foreground">Data</th>
                  <th className="text-left p-4 font-medium text-foreground">Fontes</th>
                  <th className="text-center p-4 font-medium text-foreground">Coletadas</th>
                  <th className="text-center p-4 font-medium text-foreground">Inseridas</th>
                  <th className="text-center p-4 font-medium text-foreground">Dupes</th>
                  <th className="text-center p-4 font-medium text-foreground">Erros</th>
                  <th className="text-left p-4 font-medium text-foreground">Duração</th>
                  <th className="text-left p-4 font-medium text-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {(periodDays ? runs.filter((r) => new Date(r.started_at) >= startOfWeek(periodDays)) : runs).map((run) => {
                  const cfg = run.status ? (statusConfig[run.status] ?? { label: run.status, icon: Bot, color: "text-muted-foreground" }) : { label: "—", icon: Bot, color: "text-muted-foreground" };
                  const Icon = cfg.icon;
                  const hasErrors = (run.total_errors ?? 0) > 0;
                  const errorDetail = (run as any).error_details;
                  const isExpanded = expandedErrors.has(run.id);
                  const parsedErrors = (() => {
                    if (!errorDetail) return null;
                    if (typeof errorDetail === "string") return [errorDetail];
                    if (Array.isArray(errorDetail)) return errorDetail;
                    if (typeof errorDetail === "object") return Object.entries(errorDetail).map(([k, v]) => `${k}: ${v}`);
                    return [String(errorDetail)];
                  })();
                  return (
                    <>
                      <tr key={run.id} className="border-b border-border last:border-0 hover:bg-secondary/30 transition-colors">
                        <td className="p-4 text-muted-foreground whitespace-nowrap">{new Date(run.started_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</td>
                        <td className="p-4 max-w-[200px]"><span className="text-foreground truncate block">{run.source}</span></td>
                        <td className="p-4 text-center font-medium text-foreground">{run.total_fetched ?? "—"}</td>
                        <td className="p-4 text-center font-medium text-green-600">{run.total_inserted ?? "—"}</td>
                        <td className="p-4 text-center text-muted-foreground">{run.total_dupes ?? "—"}</td>
                        <td className="p-4 text-center">
                          {hasErrors ? (
                            <button onClick={() => toggleError(run.id)} className="inline-flex items-center gap-1 text-destructive hover:underline">
                              {run.total_errors}
                              {errorDetail ? (isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />) : null}
                            </button>
                          ) : <span className="text-muted-foreground">0</span>}
                        </td>
                        <td className="p-4 text-muted-foreground whitespace-nowrap">{formatDuration(run.started_at, run.finished_at)}</td>
                        <td className="p-4"><div className={`flex items-center gap-1 ${cfg.color}`}><Icon className="w-4 h-4" /><span>{cfg.label}</span></div></td>
                      </tr>
                      {isExpanded && parsedErrors && (
                        <tr key={`${run.id}-errors`} className="bg-destructive/5">
                          <td colSpan={8} className="px-6 py-3">
                            <div className="text-xs space-y-1 max-h-48 overflow-y-auto">
                              {parsedErrors.map((err, i) => (
                                <div key={i} className="flex items-start gap-2 text-destructive">
                                  <XCircle className="w-3 h-3 mt-0.5 shrink-0" />
                                  <span className="break-all">{String(err)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Enrichment jobs with pagination, status filter, and actions */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-card rounded-2xl border border-border overflow-hidden mt-6">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-semibold text-foreground flex items-center gap-2">
              Vagas para enriquecimento de informações
              {isEnrichmentFetching && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {totalEnrichmentCount} vaga{totalEnrichmentCount !== 1 ? "s" : ""}
              {debouncedSearch && ` para "${debouncedSearch}"`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Buscar vaga..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="h-8 w-[200px] text-xs pl-8 pr-7"
              />
              {searchTerm && (
                <button
                  onClick={() => { setSearchTerm(""); setDebouncedSearch(""); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <Select value={enrichmentStatus} onValueChange={(v) => { setEnrichmentStatus(v); setPage(0); }}>
              <SelectTrigger className="h-8 w-[170px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENRICHMENT_STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(0); }}>
              <SelectTrigger className="h-8 w-[90px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>{size} / pág</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {/* Location & quality filters */}
        <div className="px-5 py-2 border-b border-border flex items-center gap-2 flex-wrap bg-secondary/20">
          <Filter className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Filtros:</span>
          <Select value={stateFilter ?? "all"} onValueChange={handleStateChange}>
            <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os estados</SelectItem>
              {availableStates.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <CityCombobox cities={availableCities} value={cityFilter} onChange={handleCityChange} />
          <Select value={qualityFilter} onValueChange={(v) => { setQualityFilter(v); setPage(0); }}>
            <SelectTrigger className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Qualquer qualidade</SelectItem>
              <SelectItem value="baixa">Baixa</SelectItem>
              <SelectItem value="media">Média</SelectItem>
              <SelectItem value="alta">Alta</SelectItem>
            </SelectContent>
          </Select>
          {(stateFilter || cityFilter || qualityFilter !== "all") && (
            <button onClick={() => { setStateFilter(null); setCityFilter(null); setQualityFilter("all"); setPage(0); }} className="text-xs text-destructive hover:underline ml-auto">
              Limpar filtros
            </button>
          )}
        </div>

        {/* Batch action bar */}
        {someSelected && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="px-5 py-3 border-b border-primary/20 bg-primary/5 flex items-center gap-3 flex-wrap"
          >
            <span className="text-sm font-medium text-foreground">{selected.size} selecionada(s)</span>
            <Button
              size="sm"
              onClick={() => batchUpdate.mutate({ ids: [...selected], status: "em_revisao" })}
              disabled={batchUpdate.isPending}
            >
              {batchUpdate.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Aprovar p/ revisão
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setConfirmDiscard({ type: "batch", ids: [...selected] })}
              disabled={batchUpdate.isPending}
            >
              {batchUpdate.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <X className="w-4 h-4 mr-1" />} Descartar
            </Button>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Limpar
            </Button>
          </motion.div>
        )}

        {filteredEnrichmentJobs.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {searchTerm ? "Nenhuma vaga encontrada para essa busca." : "Nenhuma vaga neste filtro. Tudo limpo! 🎉"}
            </p>
          </div>
        ) : (
          <>
            {/* Select all row */}
            <div className="px-5 py-2 border-b border-border flex items-center gap-3 bg-secondary/30">
              <Checkbox
                checked={actionableIds.length > 0 && actionableIds.every((id) => selected.has(id))}
                onCheckedChange={() => selectAllVisible()}
              />
              <span className="text-xs text-muted-foreground">Selecionar página ({actionableIds.length})</span>
              <button
                onClick={selectAllCrossPage}
                className="text-xs text-primary hover:underline"
              >
                {selected.size >= totalEnrichmentCount && totalEnrichmentCount > 0 ? "Desmarcar todas" : `Selecionar todas (${Math.min(totalEnrichmentCount, 500)}) · máx. 500`}
              </button>
            </div>
            <div className="divide-y divide-border">
              {filteredEnrichmentJobs.map((job) => {
                const meta = (job as any).crawler_metadata?.[0];
                const isActionable = ENRICHMENT_ACTIONABLE_STATUSES.includes(job.status);
                const isSelected = selected.has(job.id);
                return (
                  <div key={job.id} className={`px-5 py-3 flex items-center gap-3 hover:bg-secondary/30 transition-colors ${isSelected ? "bg-primary/5" : ""}`}>
                    {isActionable && (
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(job.id)}
                      />
                    )}
                    {!isActionable && <div className="w-4" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-foreground truncate">{job.title}</p>
                        {(() => {
                          const q = getJobQuality(job);
                          const qStyle = QUALITY_STYLES[q.tier];
                          return (
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${qStyle.bg} ${qStyle.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${qStyle.dot}`} />
                              {qStyle.label} ({q.filled}/{q.total})
                            </span>
                          );
                        })()}
                      </div>
                      <p className="text-xs text-muted-foreground truncate flex items-center gap-1.5 flex-wrap">
                        {meta?.company_name ?? "—"}
                        <SourceBadge sourceName={meta?.source_name} sourceType={meta?.source_type} size="sm" showSourceName />
                        <WorkModelBadge model={job.work_model} size="sm" />
                        <DeadlineBadge deadline={job.deadline_date} size="sm" />
                        {isSearching && (job as any)._match_rank && (
                          <span className="ml-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-medium">
                            {MATCH_RANK_LABELS[(job as any)._match_rank] ?? "match"}
                          </span>
                        )}
                      </p>
                      {(() => {
                        const q = getJobQuality(job);
                        const pct = Math.round((q.filled / q.total) * 100);
                        return (
                          <div className="flex items-center gap-2 mt-1">
                            <SlidersHorizontal className="w-3 h-3 text-muted-foreground shrink-0" />
                            <span className="text-[10px] text-muted-foreground whitespace-nowrap">{q.filled}/{q.total} campos</span>
                            <Progress value={pct} className="h-1.5 flex-1 max-w-[200px]" />
                            <span className="text-[10px] text-muted-foreground">{pct}%</span>
                          </div>
                        );
                      })()}
                    </div>
                    <span className={`text-xs font-medium whitespace-nowrap ${enrichmentStatusColors[job.status] ?? "text-muted-foreground"}`}>
                      {enrichmentStatusLabels[job.status] ?? job.status}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      {meta?.external_apply_url && (
                        <Button size="sm" variant="ghost" className="h-7 px-2" asChild>
                          <a href={meta.external_apply_url} target="_blank" rel="noopener noreferrer"><Eye className="w-3.5 h-3.5" /></a>
                        </Button>
                      )}
                      {isActionable && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => navigate(`/admin/robo-vagas/${job.id}/editar`)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            className="h-7 px-2"
                            onClick={() => updateStatus.mutate({ id: job.id, status: "em_revisao" })}
                            disabled={updateStatus.isPending}
                            title="Enviar para revisão"
                          >
                            {updateStatus.isPending && updateStatus.variables?.id === job.id && updateStatus.variables?.status === "em_revisao"
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Check className="w-3.5 h-3.5" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-7 px-2"
                            onClick={() => setConfirmDiscard({ type: "single", id: job.id })}
                            disabled={updateStatus.isPending}
                            title="Descartar vaga"
                          >
                            {updateStatus.isPending && updateStatus.variables?.id === job.id && updateStatus.variables?.status === "descartada"
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <X className="w-3.5 h-3.5" />}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-border flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Página {page + 1} de {totalPages}
            </p>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" className="h-7 px-2" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {/* Show max 5 page buttons */}
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const startPage = Math.max(0, Math.min(page - 2, totalPages - 5));
                const p = startPage + i;
                return (
                  <Button
                    key={p}
                    size="sm"
                    variant={p === page ? "default" : "ghost"}
                    className="h-7 w-7 px-0 text-xs"
                    onClick={() => setPage(p)}
                  >
                    {p + 1}
                  </Button>
                );
              })}
              <Button size="sm" variant="ghost" className="h-7 px-2" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Confirm discard dialog */}
      <AlertDialog open={!!confirmDiscard} onOpenChange={(open) => !open && setConfirmDiscard(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Descartar {confirmDiscard?.type === "batch" ? `${confirmDiscard.ids.length} vaga(s)` : "vaga"}?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDiscard?.type === "batch"
                ? `Tem certeza que deseja descartar ${confirmDiscard.ids.length} vaga(s) selecionada(s)? Essa ação pode ser revertida alterando o status depois.`
                : "Tem certeza que deseja descartar esta vaga? Essa ação pode ser revertida alterando o status depois."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!confirmDiscard) return;
                if (confirmDiscard.type === "batch") {
                  batchUpdate.mutate({ ids: confirmDiscard.ids, status: "descartada" });
                } else {
                  updateStatus.mutate({ id: confirmDiscard.id, status: "descartada" });
                }
                setConfirmDiscard(null);
              }}
            >
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
